import { Container, Graphics } from 'pixi.js';
import { isReducedMotion } from './reduced-motion';

/**
 * Warm amber — the universal "you can use this" affordance. Matches the HUD's amber prompt language, so a
 * highlighted object on the canvas and the DOM "Press E" prompt read as the same cue. Theme-independent on
 * purpose: the interactivity signal should look the same in every world (Office HQ / Station / Academy / Café).
 */
const RING_COLOR = 0xffd479;

/**
 * A soft, pulsing floor ring that marks the world object or seat the local duck can USE right now (the
 * thing "E" acts on). It's the in-world half of the interact affordance — paired with the DOM "Press E"
 * prompt so the cue is never canvas-only (accessibility: a colour-blind / reduced-motion player still has
 * the dual-coded text prompt + this ring stays visible, just un-animated).
 *
 * Built exactly like the duck speaking-ring (the codebase's established emphasis primitive): a single
 * Graphics, animated with TRANSFORM only (scale + alpha) off the engine ticker — no filters, no per-frame
 * redraw — so it's effectively free to run. Reduced-motion safe (steady, but still clearly visible).
 */
export class InteractHighlight {
  readonly root = new Container();
  private phase = 0;
  private shown = false;

  constructor() {
    // A crisp accent ring + a faint inner wash, so it reads as a glowing pad under the prop/seat.
    const g = new Graphics()
      .circle(0, 0, 30)
      .stroke({ color: RING_COLOR, width: 3, alpha: 0.9 })
      .circle(0, 0, 23)
      .fill({ color: RING_COLOR, alpha: 0.1 });
    this.root.addChild(g);
    this.root.visible = false;
  }

  /** Place + reveal the ring at a world position (idempotent — safe to call every frame). */
  showAt(x: number, y: number): void {
    this.root.position.set(x, y);
    if (!this.shown) {
      this.shown = true;
      this.root.visible = true;
      this.phase = 0; // start the pulse from its calm low so a fresh target eases in
    }
  }

  /** Tuck the ring away (idempotent). */
  hide(): void {
    if (!this.shown) return;
    this.shown = false;
    this.root.visible = false;
    this.root.scale.set(1);
    this.root.alpha = 1;
  }

  /** Per-frame pulse (scale + alpha). Steady-but-visible under reduced motion (no movement). */
  update(dtMs: number): void {
    if (!this.shown) return;
    if (isReducedMotion()) {
      this.root.scale.set(1.06);
      this.root.alpha = 0.85;
      return;
    }
    this.phase += dtMs * 0.005;
    const t = (Math.sin(this.phase) + 1) / 2; // 0..1 sine ease
    this.root.scale.set(1 + t * 0.16); // breathe 1.0 → 1.16
    this.root.alpha = 0.6 + t * 0.4; // 0.6 → 1.0
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}
